import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useParams } from "react-router";

import CreateGeometryType from "./CreateGeometryType.graphql";
import * as UI from "../../../../../modules/ui-kit";
import EntityCreateForm from "../../../components/EntityCreateForm";
import {
    PropertiesField,
    RequirementGroupsField,
    UrlListField,
} from "../../../components/fields";
import ListGeometryTypes from "../../BimClass/BimClassDetail/ListGeometryTypes.graphql";

const DEFAULT_VALUES = {
    deprecated: false,
    name: [],
    previews: [],
    properties: [],
    requirementGroups: [],
};

function toDefinition(values) {
    const definition: Record<string, unknown> = {
        deprecated: values.deprecated,
        name: values.name,
    };
    // Only send the collections that were actually filled in — an empty array
    // is a meaningful "cleared" value on update, but noise on create.
    // Stored as `previews`; the GraphQL type only exposes it as `images`.
    if (values.previews.length) definition.previews = values.previews;
    if (values.requirementGroups.length) definition.requirementGroups = values.requirementGroups;
    if (values.properties.length) definition.properties = values.properties;
    return definition;
}

function renderFields({ saving, setValue, values }) {
    return (
        <>
            <UI.Field>
                <UI.FieldLabel>name</UI.FieldLabel>
                <UI.FieldDescription>Multi-language translations for the Geometry Type name</UI.FieldDescription>
                <UI.TranslatedInput
                    disabled={ saving }
                    label="name"
                    onChange={ (val) => setValue("name", val) }
                    value={ values.name }
                />
            </UI.Field>

            <UrlListField
                addLabel="Add Preview Image URL"
                description="Preview image URLs illustrating this Geometry Type"
                disabled={ saving }
                label="previews"
                onChange={ (val) => setValue("previews", val) }
                value={ values.previews }
            />

            <RequirementGroupsField
                description="Logical groups organizing parameters for this Geometry Type"
                disabled={ saving }
                label="requirementGroups"
                onChange={ (val) => setValue("requirementGroups", val) }
                value={ values.requirementGroups }
            />

            <PropertiesField
                description="Assigned parameter properties and values (parameter.id &amp; value)"
                disabled={ saving }
                label="properties"
                onChange={ (val) => setValue("properties", val) }
                value={ values.properties }
            />

            {/* Last, matching the detail editor. */}
            <UI.Field>
                <UI.FieldLabel>deprecated</UI.FieldLabel>
                <UI.FieldDescription>Toggle whether this Geometry Type is active or deprecated</UI.FieldDescription>
                <UI.SegmentedControl
                    disabled={ saving }
                    onChange={ (val) => setValue("deprecated", val) }
                    options={ [
                        { label: "Deprecated", value: true },
                        { label: "Not deprecated", value: false },
                    ] }
                    value={ values.deprecated }
                />
            </UI.Field>
        </>
    );
}

export default function GeometryTypeNew() {
    const { bimClassID } = useParams();

    const [createGeometryType] = useMutation(
        gql(CreateGeometryType),
        {
            awaitRefetchQueries: true,
            // Document + variables, not the query name: this screen replaces the
            // list rather than sitting beside it, so there is no active query for
            // a name to match and the refetch was silently a no-op.
            refetchQueries: [
                { query: gql(ListGeometryTypes), variables: { id: bimClassID } },
            ],
        },
    );

    async function onCreate(geometryType) {
        const result = await createGeometryType({
            variables: {
                bimClassID,
                geometryType,
            },
        });
        const payload = result.data?.createGeometryType;
        const geometryTypeID = payload?.geometryType?.id;
        const revisionID = payload?.geometryTypeRevision?.id;
        return {
            errors: payload?.errors,
            redirectTo: geometryTypeID && revisionID
                && `../${ geometryTypeID }/revisions/${ revisionID }`,
        };
    }

    return (
        <div className="section-card">
            <EntityCreateForm
                createLabel="Create Geometry Type"
                defaultValues={ DEFAULT_VALUES }
                jsonDescription="Raw Geometry Type revision JSON definition (name, deprecated, previews, requirementGroups, properties)"
                onCreate={ onCreate }
                renderFields={ renderFields }
                title="New Geometry Type"
                toDefinition={ toDefinition }
            />
        </div>
    );
}
